import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getPath, addPath, updatePath, clearCurrentPath } from '../actions/pathActions';
import { getCoursesMinimal } from '../actions/courseActions';
import PathForm from '../components/path/PathForm';
import ContentManagementSidebar from '../components/content-management/ContentManagementSidebar';
import { isAdminRole } from '../utils/roleHelpers';

const PathEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const { currentPath, loading: pathLoading } = useSelector(state => state.path);
  const { coursesMinimal, loading: courseLoading } = useSelector(state => state.course);
  const [showForm, setShowForm] = useState(false);

  // Determine mode from URL path
  const isView = location.pathname.includes('/view');
  const isEdit = location.pathname.includes('/edit');
  const isNew = location.pathname.includes('/new');
  const mode = isView ? 'view' : isEdit ? 'edit' : 'new';

  // Role detection
  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';

  useEffect(() => {
    const loadData = async () => {
      try {
        // Always get minimal courses for the dropdown
        await dispatch(getCoursesMinimal());

        // If we have an ID, load single path
        if (id) {
          await dispatch(getPath(id));
        }
      } catch (error) {
        console.error('Error loading data:', error);
      }
    };

    loadData();

    // Show form after short delay to ensure data is loaded
    const timer = setTimeout(() => {
      setShowForm(true);
    }, 100);

    // Cleanup: clear current path when component unmounts
    return () => {
      clearTimeout(timer);
      dispatch(clearCurrentPath());
    };
  }, [dispatch, id]);

  const handleClose = () => {
    navigate('/content-management/paths');
  };

  const handleSubmit = async (pathData) => {
    try {
      if (isEdit && id) {
        await dispatch(updatePath(id, pathData));
      } else if (isNew) {
        await dispatch(addPath(pathData));
      }
      // Navigate back to paths list after successful save
      navigate('/content-management/paths');
    } catch (error) {
      console.error('Error saving path:', error);
      throw error;
    }
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Paths') {
      navigate('/content-management/paths');
    } else {
      navigate('/content-management', { state: { activeTab: tabId } });
    }
  };

  // Show loading spinner while data is being fetched
  if ((isEdit || isView) && pathLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Paths"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={isInstructor}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 pb-6 px-6 min-w-0">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1086a4]"></div>
              <span className="ml-4 text-gray-600">Loading path...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show form only after data is ready
  if (!showForm) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
        <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
          <ContentManagementSidebar
            activeTab="Paths"
            onTabChange={handleTabChange}
            isAdmin={isAdmin}
            isInstructor={isInstructor}
          />
          <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 pb-6 px-6 min-w-0">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1086a4]"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="w-full bg-white rounded-lg shadow-sm flex flex-col md:flex-row">
        <ContentManagementSidebar
          activeTab="Paths"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
        />
        <div className="flex-1 border-t md:border-t-0 md:border-l border-gray-200 min-w-0">
          <PathForm
            path={currentPath}
            courses={coursesMinimal}
            onClose={handleClose}
            onSubmit={handleSubmit}
            mode={mode}
          />
        </div>
      </div>
    </div>
  );
};

export default PathEditor; 